import { z } from 'zod';
import { ISODateSchema } from './common';
import { ImageWithContentTypeSchema } from './file';
import { GeographicOriginSchema, GeographicRegionSchema } from './geographic-market-data';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
import { AssetRatingSchema, ComparablesSchema, ValueOverviewSensitivityMatrixSchema } from './value-overview';
import { ExistingEinwertValuationSchema, ExistingExternalValuationSchema, ValuationQualitySchema } from './valuation';
import { CombinedCostValuationSchema, CostValuationParametersSchema } from './valuation-cost';
import { CombinedComparisonValuationSchema } from './valuation-comparison';
import { SummaryRisksSchema, ValuationEconomicUnitSchema } from './valuation-details';
import { FactsSchema } from './valuation-facts';
import { CombinedIncomeValuationSchema, ReadIncomeValuationParametersSchema } from './valuation-income';
import { BaseValuationStructureSchema, CombinedLandValuationSchema, SpecialPropertyFeatureSchema, ValuationLandSchema, ValuationStructureSchema, } from './valuation-property';
import { ManagementCostParameterSchema, TenantAggregateSchema, TenantSchema } from './valuation-tenants';
const CustomerRentFreePeriodSchema = z.object({
    start_date: ISODateSchema,
    end_date: ISODateSchema,
});
const CustomerVacancyPeriodSchema = z.object({
    start_date: ISODateSchema.optional(),
    end_date: ISODateSchema,
});
const SharedRentalAgreements = TenantSchema.shape.rental_agreement.unwrap().options;
const CustomerFixedRentalAgreementSchema = SharedRentalAgreements[0].extend({
    rent_free_periods: CustomerRentFreePeriodSchema.array().optional(),
});
const CustomerIndexedRentalAgreementSchema = SharedRentalAgreements[1].extend({
    rent_free_periods: CustomerRentFreePeriodSchema.array().optional(),
});
const CustomerUnknownRentalAgreementSchema = SharedRentalAgreements[2].extend({
    rent_free_periods: CustomerRentFreePeriodSchema.array().optional(),
});
const CustomerGraduatedRentalAgreementSchema = SharedRentalAgreements[3].extend({
    rent_free_periods: CustomerRentFreePeriodSchema.array().optional(),
});
const CustomerRentalAgreementSchema = z.union([
    CustomerFixedRentalAgreementSchema,
    CustomerIndexedRentalAgreementSchema,
    CustomerUnknownRentalAgreementSchema,
    CustomerGraduatedRentalAgreementSchema,
]);
export const CustomerTenantSchema = TenantSchema.omit({ category: true }).extend({
    rental_agreement: CustomerRentalAgreementSchema.optional(),
    vacancy_periods: CustomerVacancyPeriodSchema.array(),
});
export const CustomerBaseValuationStructureSchema = BaseValuationStructureSchema.omit({
    construction_year_description: true,
});
export const CustomerValuationStructureSchema = ValuationStructureSchema.omit({
    construction_year_description: true,
}).extend({
    tenantsV3: CustomerTenantSchema.array(),
});
const CustomerManagementCostParameterSchema = z.union([
    ManagementCostParameterSchema.options[0],
    ManagementCostParameterSchema.options[1],
    ManagementCostParameterSchema.options[2],
    ManagementCostParameterSchema.options[3],
    ManagementCostParameterSchema.options[4],
]);
export const CustomerManagementCostsSchema = z.object({
    administrative_cost: CustomerManagementCostParameterSchema.optional(),
    maintenance_cost: CustomerManagementCostParameterSchema.optional(),
    operating_cost: CustomerManagementCostParameterSchema.optional(),
    loss_of_rent_risk: CustomerManagementCostParameterSchema.optional(),
});
const SharedTenantParameterSchema = ReadIncomeValuationParametersSchema.shape.tenant_parameters.element;
export const CustomerMortgageManagementCostsSchema = CustomerManagementCostsSchema.extend({
    modernization_risk: SharedTenantParameterSchema.shape.mortgage_management_costs.shape.modernization_risk,
});
export const CustomerIncomeTenantParameterSchema = SharedTenantParameterSchema.extend({
    management_costs: CustomerManagementCostsSchema,
    mortgage_management_costs: CustomerMortgageManagementCostsSchema,
});
const CustomerBaseIncomeValuationParametersSchema = z.object({
    tenant_parameters: CustomerIncomeTenantParameterSchema.array(),
});
export const CustomerIncomeValuationParametersSchema = ReadIncomeValuationParametersSchema.omit({ descriptions: true }).extend({
    tenant_parameters: CustomerIncomeTenantParameterSchema.array(),
});
export const CustomerBaseValuationEconomicUnitSchema = z.object({
    structures: CustomerBaseValuationStructureSchema.array(),
    income_valuation_parameters: CustomerBaseIncomeValuationParametersSchema,
});
export const CustomerExternalValuationEconomicUnitSchema = CustomerBaseValuationEconomicUnitSchema.extend({
    tenantsV3: CustomerTenantSchema.array(),
});
const SharedValuationAreaSchema = ValuationLandSchema.shape.areas.element;
export const CustomerStandardLandValueSchema = SharedValuationAreaSchema.shape.standard_land_value.extend({ date: z.string().optional() });
export const CustomerValuationAreaSchema = SharedValuationAreaSchema.omit({
    adjustment_factors: true,
    market_adjustment_factor: true,
    mortgage_lending_market_adjustment_factor: true,
    market_adjusted_land_value: true,
}).extend({
    standard_land_value: CustomerStandardLandValueSchema,
    conversion_factor: z.number(),
    economy_factor: z.number(),
    layout_factor: z.number(),
    other_adjustments_factor: z.number(),
    description: z.string().optional(),
});
export const CustomerValuationLandSchema = ValuationLandSchema.extend({
    areas: CustomerValuationAreaSchema.array(),
});
const CustomerYieldLandValuationSchema = CombinedLandValuationSchema.shape.market.shape.yielding
    .omit({ valuation_land_areas_values_summaries: true })
    .extend({ valuation_land_areas: CustomerValuationAreaSchema.array() });
const CustomerLandValuationSchema = CombinedLandValuationSchema.shape.market.extend({
    yielding: CustomerYieldLandValuationSchema,
    non_yielding: CustomerYieldLandValuationSchema,
});
const CustomerCombinedLandValuationSchema = z.object({
    market: CustomerLandValuationSchema,
    mortgage: CustomerLandValuationSchema,
});
export const CustomerSpecialPropertyFeatureSchema = SpecialPropertyFeatureSchema.omit({
    sub_type: true,
}).extend({
    feature_type: z.string(),
});
export const CustomerCostValuationStructureParameterSchema = CostValuationParametersSchema.shape.market.shape.structure_parameters.element.omit({
    revaluation_parameters: true,
    structure_value_rounding: true,
});
export const CustomerCostValuationParameterSetSchema = CostValuationParametersSchema.shape.market.extend({
    construction_cost_type: z.enum(['bki', 'nhk']),
    structure_parameters: CustomerCostValuationStructureParameterSchema.array(),
});
export const CustomerCostValuationParametersSchema = z.object({
    market: CustomerCostValuationParameterSetSchema,
    mortgage: CustomerCostValuationParameterSetSchema,
});
const SharedMarketStructureCosts = CombinedCostValuationSchema.shape.market.shape.structure_construction_costs.element.options;
const SharedMortgageStructureCosts = CombinedCostValuationSchema.shape.mortgage.shape.structure_construction_costs.element.options;
export const CustomerNhkMarketStructureCostSchema = SharedMarketStructureCosts[0].omit({
    structure_value_rounding: true,
    combined_construction_value_rounded: true,
});
const CustomerBkiMarketStructureCostSchema = SharedMarketStructureCosts[1].omit({
    structure_value_rounding: true,
    combined_construction_value_rounded: true,
});
export const CustomerNhkMortgageStructureCostSchema = SharedMortgageStructureCosts[0].omit({ structure_value_rounding: true });
const CustomerBkiMortgageStructureCostSchema = SharedMortgageStructureCosts[1].omit({
    structure_value_rounding: true,
});
export const CustomerMarketCostValuationSchema = CombinedCostValuationSchema.shape.market
    .omit({ special_property_features_value: true, combined_construction_value_rounded: true })
    .extend({ structure_construction_costs: z.union([CustomerBkiMarketStructureCostSchema, CustomerNhkMarketStructureCostSchema]).array() });
export const CustomerMortgageCostValuationSchema = CombinedCostValuationSchema.shape.mortgage.omit({ special_property_features_value: true }).extend({
    structure_construction_costs: z.union([CustomerBkiMortgageStructureCostSchema, CustomerNhkMortgageStructureCostSchema]).array(),
});
export const CustomerCombinedCostValuationSchema = z.object({
    market: CustomerMarketCostValuationSchema,
    mortgage: CustomerMortgageCostValuationSchema,
});
const CustomerComparisonValuationSchema = CombinedComparisonValuationSchema.shape.market.omit({ special_property_features_value: true });
const CustomerMortgageComparisonValuationSchema = CombinedComparisonValuationSchema.shape.mortgage.omit({
    special_property_features_value: true,
});
const CustomerCombinedComparisonValuationSchema = z.object({
    market: CustomerComparisonValuationSchema,
    mortgage: CustomerMortgageComparisonValuationSchema,
});
export const CustomerRentSummarySchema = TenantAggregateSchema.shape.contract_rent.extend({
    value_per_area: SquareMetreMoneyPerAreaValueSchema.optional(),
});
export const CustomerTenantAggregateSchema = TenantAggregateSchema.omit({
    rental_status: true,
}).extend({
    contract_rent: CustomerRentSummarySchema,
});
export const CustomerTenantSummarySchema = z.object({
    tenant_aggregates: CustomerTenantAggregateSchema.array(),
    total: CustomerTenantAggregateSchema,
});
const SharedIncomeTenantAggregateSchema = CombinedIncomeValuationSchema.shape.market.shape.tenant_summary.shape.total;
export const CustomerIncomeTenantAggregateSchema = CustomerTenantAggregateSchema.extend({
    annual_gross_income: CustomerRentSummarySchema,
    discount_rates: SharedIncomeTenantAggregateSchema.shape.discount_rates,
    management_costs: SharedIncomeTenantAggregateSchema.shape.management_costs,
});
export const CustomerIncomeTenantSummarySchema = z.object({
    tenant_aggregates: CustomerIncomeTenantAggregateSchema.array(),
    total: CustomerIncomeTenantAggregateSchema,
});
export const CustomerIncomeValuationSchema = CombinedIncomeValuationSchema.shape.market
    .omit({
    special_property_features_value: true,
    management_costs: true,
    use_weighted_approach: true,
    weighted_discount_rate: true,
    weighted_multiplier: true,
    weighted_residual_life_span: true,
    weighted_return_on_land_value: true,
    weighted_net_income_construction: true,
    gross_yield: true,
    gross_initial_yield: true,
    kpis: true,
    over_under_rent_results: true,
    vacancy_deduction_results: true,
    rent_free_time_deduction_result: true,
})
    .extend({ tenant_summary: CustomerIncomeTenantSummarySchema });
const SharedIncomeMortgageSchema = CombinedIncomeValuationSchema.shape.mortgage.unwrap();
export const CustomerIncomeMortgageValuationSchema = CustomerIncomeValuationSchema.extend({
    structures: SharedIncomeMortgageSchema.shape.structures,
    land_value_minus_demolition_cost: SharedIncomeMortgageSchema.shape.land_value_minus_demolition_cost,
    land_value_minus_demolition_cost_discount_factor: SharedIncomeMortgageSchema.shape.land_value_minus_demolition_cost_discount_factor,
    discounted_land_value_minus_demolition_cost: SharedIncomeMortgageSchema.shape.discounted_land_value_minus_demolition_cost,
});
export const CustomerCombinedIncomeValuationSchema = CombinedIncomeValuationSchema.extend({
    market: CustomerIncomeValuationSchema,
    mortgage: CustomerIncomeMortgageValuationSchema.optional(),
    sensitivity_matrix: ValueOverviewSensitivityMatrixSchema.optional(),
});
export const CustomerPointOfInterestFactSchema = FactsSchema.shape.points_of_interest.element.extend({ origin: GeographicOriginSchema });
const SharedTimeSeries = FactsSchema.shape.time_series.element.options;
const CustomerNumberTimeSeriesSchema = SharedTimeSeries[0].extend({
    origin: GeographicOriginSchema,
    region: GeographicRegionSchema,
});
const CustomerPercentageTimeSeriesSchema = SharedTimeSeries[1].extend({
    origin: GeographicOriginSchema,
    region: GeographicRegionSchema,
});
const CustomerMonetaryTimeSeriesSchema = SharedTimeSeries[2].extend({
    origin: GeographicOriginSchema,
    region: GeographicRegionSchema,
});
const CustomerMoneyPerAreaTimeSeriesSchema = SharedTimeSeries[3].extend({
    origin: GeographicOriginSchema,
    region: GeographicRegionSchema,
});
export const CustomerTimeSeriesFactSchema = z.union([
    CustomerNumberTimeSeriesSchema,
    CustomerPercentageTimeSeriesSchema,
    CustomerMonetaryTimeSeriesSchema,
    CustomerMoneyPerAreaTimeSeriesSchema,
]);
export const CustomerValuationFactsSchema = FactsSchema.omit({
    emissions_immissions: true,
}).extend({
    points_of_interest: CustomerPointOfInterestFactSchema.array(),
    time_series: CustomerTimeSeriesFactSchema.array(),
});
const CustomerRentSituationSchema = SummaryRisksSchema.shape.rent_situation.unwrap().exclude(['']).optional();
export const CustomerSummaryRisksSchema = SummaryRisksSchema.extend({
    rent_situation: CustomerRentSituationSchema,
});
export const CustomerMarketDataItemSchema = ValuationEconomicUnitSchema.shape.market_data
    .unwrap()
    .element.extend({ range: z.custom().optional() });
export const CustomerValuationEconomicUnitSchema = ValuationEconomicUnitSchema.omit({
    structure_description: true,
    structure_change_from_previous_valuation: true,
    special_property_features_change_from_previous_valuation: true,
    land_value_change_from_previous_valuation: true,
    result_plausibility: true,
    result_change_from_previous_valuation: true,
}).extend({
    special_property_features: CustomerSpecialPropertyFeatureSchema.array(),
    structures: CustomerValuationStructureSchema.array(),
    land: CustomerValuationLandSchema,
    cost_valuation_parameters: CustomerCostValuationParametersSchema,
    income_valuation_parameters: CustomerIncomeValuationParametersSchema,
    special_property_features_value: z.object({ market: MonetaryValueSchema, mortgage: MonetaryValueSchema }),
    land_valuation: CustomerCombinedLandValuationSchema,
    comparison_valuation: CustomerCombinedComparisonValuationSchema,
    cost_valuation: CustomerCombinedCostValuationSchema,
    income_valuation: CustomerCombinedIncomeValuationSchema,
    tenant_summary: CustomerTenantSummarySchema,
    comparables: ComparablesSchema.optional(),
    market_data: CustomerMarketDataItemSchema.array().optional(),
});
export const CustomerExternalValuationRatingsSchema = ExistingExternalValuationSchema.shape.rating_summary
    .unwrap()
    .extend({ structures: ValuationQualitySchema, location: ValuationQualitySchema });
const CustomerBaseValuationFields = {
    images: ImageWithContentTypeSchema.array(),
    income_approach_sensitivity_matrix: ValueOverviewSensitivityMatrixSchema.optional(),
    tenant_summary: ExistingEinwertValuationSchema.shape.tenant_summary.unwrap(),
    gross_rental_income: MonetaryValueSchema.optional(),
    comparables: ComparablesSchema.optional(),
    rating_summary: z.union([AssetRatingSchema, CustomerExternalValuationRatingsSchema]).optional(),
    capabilities: ExistingEinwertValuationSchema.shape.capabilities.optional(),
};
export const CustomerExistingEinwertValuationSchema = ExistingEinwertValuationSchema.omit({
    acquisition_costs: true,
    assumptions: true,
    earthquake_zone: true,
    inspection_description: true,
    land_description: true,
    standard_land_value_zones: true,
}).extend({
    ...CustomerBaseValuationFields,
    facts: CustomerValuationFactsSchema,
    summary_risks: CustomerSummaryRisksSchema.optional(),
    economic_units: z.intersection(CustomerValuationEconomicUnitSchema.array(), CustomerBaseValuationEconomicUnitSchema.array()),
    structure_description: z.string().optional(),
});
export const CustomerExistingExternalValuationSchema = ExistingExternalValuationSchema.omit({
    acquisition_costs: true,
    submission_trigger: true,
}).extend({ ...CustomerBaseValuationFields, economic_units: CustomerExternalValuationEconomicUnitSchema.array() });
export const CustomerExistingHistoricalValuationSchema = CustomerExistingExternalValuationSchema.extend({
    valuation_type: z.literal('historical'),
});
export const CustomerExistingValuationSchema = z.discriminatedUnion('valuation_type', [
    CustomerExistingEinwertValuationSchema,
    CustomerExistingExternalValuationSchema,
    CustomerExistingHistoricalValuationSchema,
]);
export const CustomerEinwertValuationSchema = CustomerExistingEinwertValuationSchema.omit({
    uuid: true,
}).extend({
    mortgage_lending_value: MonetaryValueSchema.optional(),
});
export const CustomerNhkStructureConstructionCostSchema = CustomerNhkMarketStructureCostSchema.pick({
    structure_cost_type: true,
    base_construction_cost_per_sqm: true,
    depreciation_factor: true,
    depreciation_value: true,
    depreciated_construction_value: true,
    exterior_construction_value: true,
    combined_construction_value: true,
    manufacturing_cost_id: true,
    model_adjustment_factor: true,
    model_adjusted_construction_cost_per_sqm: true,
    extra_construction_cost: true,
});
